/* Importa gli embed pubblicati da altri bot (es. MEE6): ricerca nel server, file, modelli, ripubblicazione. */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Check, Download, ExternalLink, Eye, FolderInput, Hash, Loader2, Pencil, Save, Search, Send, Upload } from "lucide-react";
import { apiError, botGet, botPost, http, templates } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DiscordEmbedPreview } from "@/components/DiscordEmbedPreview";
import { outlineBtn, primaryBtn } from "@/components/kit";
import { InfoTip } from "@/pages/tickets/ticketsKit";

const scanApi = {
  start: () => botPost("embed-export/scan", {}),
  job: (id) => botGet(`embed-export/jobs/${id}`),
};

// ── immagini: i link di Discord scadono, quindi se ne salva una copia sul sito ──
const EXPIRING = /^https:\/\/(cdn\.discordapp\.com|media\.discordapp\.net)\//;
const copied = new Map();
async function keepImage(url) {
  if (!url || !EXPIRING.test(url)) return url;
  if (!copied.has(url)) {
    copied.set(url, http.post("/media/import", { url }).then((r) => `${window.location.origin}${r.data.path}`).catch(() => url));
  }
  return copied.get(url);
}
/** Restituisce una copia del messaggio con le immagini spostate su link che non scadono. */
async function withSafeImages(msg) {
  const embeds = await Promise.all((msg.embeds || []).map(async (e) => ({
    ...e,
    image: await keepImage(e.image), thumbnail: await keepImage(e.thumbnail),
    author: { ...e.author, iconUrl: await keepImage(e.author?.iconUrl) },
    footer: { ...e.footer, iconUrl: await keepImage(e.footer?.iconUrl) },
  })));
  return { ...msg, embeds };
}

const titleOf = (it) => it.message.embeds.find((e) => e.title)?.title || it.message.embeds.find((e) => e.author?.name)?.author.name || (it.message.content || "").slice(0, 60) || "Messaggio";
const plain = (s) => (s || "").replace(/[*_~`>#|]/g, "").replace(/<[@#][&!]?\d+>/g, "@…").trim();

function Item({ it, selected, onToggle, onOpen, onPublish, busy }) {
  const [show, setShow] = useState(false);
  const first = it.message.embeds[0];
  return (
    <div className={`rounded-xl border p-3 transition-colors ${selected ? "border-[#5865F2]/70 bg-[#5865F2]/[0.07]" : "border-[#232838] bg-[#131620]"}`}>
      <div className="flex items-start gap-3">
        <button type="button" onClick={onToggle} className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${selected ? "border-[#5865F2] bg-[#5865F2]" : "border-[#4b4d54]"}`}>{selected && <Check className="h-3.5 w-3.5 text-white" />}</button>
        <div className="w-1 self-stretch rounded-full" style={{ background: first?.color || "#4b4d54" }} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-white">{plain(titleOf(it))}</p>
          {first?.description && <p className="line-clamp-2 text-xs text-[#949BA4]">{plain(first.description)}</p>}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-[#6b7185]">
            <span>{it.authorName}</span><span>·</span><span>{new Date(it.createdAt).toLocaleDateString("it-IT")}</span>
            {it.message.embeds.length > 1 && <span className="rounded bg-[#232838] px-1.5 text-[#B5BAC1]">{it.message.embeds.length} embed</span>}
            {it.message.buttons.length > 0 && <span className="rounded bg-[#232838] px-1.5 text-[#B5BAC1]">{it.message.buttons.length} pulsanti</span>}
            {it.pinned && <span className="rounded bg-[#232838] px-1.5 text-[#B5BAC1]">📌 fissato</span>}
          </div>
          {it.notes?.length > 0 && <p className="mt-1 flex gap-1 text-[11px] text-[#FEE75C]"><AlertTriangle className="h-3 w-3 shrink-0" />{it.notes.join(" · ")}</p>}
          {it.attachments?.length > 0 && <p className="mt-1 text-[11px] text-[#FEE75C]">📎 Allegati non inclusi: {it.attachments.map((a) => a.name).join(", ")}</p>}
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1">
          <button type="button" title="Anteprima" onClick={() => setShow(!show)} className={`rounded-md p-1.5 hover:bg-white/5 ${show ? "text-white" : "text-[#949BA4]"}`}><Eye className="h-4 w-4" /></button>
          <button type="button" title="Apri nell'editor" onClick={onOpen} className="rounded-md p-1.5 text-[#949BA4] hover:bg-white/5 hover:text-white"><Pencil className="h-4 w-4" /></button>
          <button type="button" title={`Pubblica con KALYX in #${it.channelName}`} disabled={busy} onClick={onPublish} className="rounded-md p-1.5 text-[#57F287] hover:bg-[#23A55A]/10 disabled:opacity-40">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
          {it.url && <a href={it.url} target="_blank" rel="noreferrer" title="Vedi su Discord" className="rounded-md p-1.5 text-[#949BA4] hover:bg-white/5 hover:text-white"><ExternalLink className="h-4 w-4" /></a>}
        </div>
      </div>
      {show && <div className="mt-3"><DiscordEmbedPreview message={it.message} botName={it.authorName} channelName={it.channelName} fillPlaceholders={false} /></div>}
    </div>
  );
}

export default function EmbedImporter({ open, onOpenChange, onOpenInEditor, onTemplatesSaved }) {
  const { guild } = useData();
  const [job, setJob] = useState(null);
  const [items, setItems] = useState(null);
  const [bots, setBots] = useState([]);
  const [botFilter, setBotFilter] = useState(new Set());
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(null);
  const [progress, setProgress] = useState(null);
  const timer = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const showResults = (list, botList) => {
    setItems(list);
    setBots(botList);
    setBotFilter(new Set(botList.map((b) => b.id)));
    setSel(new Set(list.map((x) => x.id)));
  };

  const scan = async () => {
    try {
      const j = await scanApi.start();
      setJob(j); setItems(null);
      clearInterval(timer.current);
      timer.current = setInterval(async () => {
        try {
          const x = await scanApi.job(j.id);
          setJob(x);
          if (x.status !== "running") {
            clearInterval(timer.current);
            showResults(x.items, Object.values(x.bots).sort((a, b) => b.count - a.count));
            toast.success(`Trovati ${x.items.length} messaggi di altri bot`);
          }
        } catch (e) { clearInterval(timer.current); toast.error(apiError(e)); }
      }, 1000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (items || []).filter((it) => botFilter.has(it.authorId) && (!s || `${it.channelName} ${titleOf(it)} ${it.message.embeds.map((e) => e.description).join(" ")}`.toLowerCase().includes(s)));
  }, [items, botFilter, q]);
  const groups = useMemo(() => {
    const g = [];
    for (const it of visible) {
      const last = g[g.length - 1];
      if (last?.channelId === it.channelId) last.items.push(it);
      else g.push({ channelId: it.channelId, channelName: it.channelName, category: it.category, items: [it] });
    }
    return g;
  }, [visible]);
  const chosen = visible.filter((it) => sel.has(it.id));
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = visible.length > 0 && visible.every((it) => sel.has(it.id));

  const saveTemplates = async () => {
    if (!chosen.length) return;
    setProgress({ label: "Salvo i modelli", done: 0, total: chosen.length });
    let ok = 0;
    for (const it of chosen) {
      try {
        const payload = await withSafeImages(it.message);
        await templates.save({ name: `#${it.channelName} · ${plain(titleOf(it)).slice(0, 60)}`, payload });
        ok++;
      } catch { /* continua con gli altri */ }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setProgress(null);
    onTemplatesSaved?.();
    toast.success(`${ok} modelli salvati`, { description: "Li trovi in «I miei modelli»." });
  };

  const downloadFile = async () => {
    if (!chosen.length) return;
    setProgress({ label: "Preparo il file (copio le immagini)", done: 0, total: chosen.length });
    const out = [];
    for (const it of chosen) {
      out.push({ ...it, message: await withSafeImages(it.message) });
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setProgress(null);
    const file = { kalyx: "embeds", version: 1, exportedAt: new Date().toISOString(), guildName: guild?.name, bots, items: out };
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(file, null, 2)], { type: "application/json" }));
    a.download = `embed-${(guild?.name || "server").replace(/\W+/g, "-").toLowerCase()}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 30000);
  };

  const uploadFile = (f) => {
    if (!f) return;
    const r = new FileReader();
    r.onload = () => {
      try {
        const data = JSON.parse(r.result);
        if (data.kalyx !== "embeds" || !Array.isArray(data.items)) throw new Error("x");
        const botList = data.bots?.length ? data.bots : [...new Map(data.items.map((it) => [it.authorId, { id: it.authorId, name: it.authorName, count: data.items.filter((x) => x.authorId === it.authorId).length }])).values()];
        showResults(data.items, botList);
        setJob(null);
        toast.success(`File caricato: ${data.items.length} messaggi`);
      } catch { toast.error("Questo file non è un'esportazione di embed di KALYX"); } finally { if (fileRef.current) fileRef.current.value = ""; }
    };
    r.readAsText(f);
  };

  const publish = async (it) => {
    setBusy(it.id);
    try {
      const msg = await withSafeImages(it.message);
      await botPost(`channels/${it.channelId}/messages`, { ...msg, content: msg.content || undefined });
      toast.success(`Pubblicato in #${it.channelName} con KALYX`, { description: "Il messaggio vecchio dell'altro bot è ancora lì: puoi eliminarlo dalla chat del canale." });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const openInEditor = async (it) => {
    const msg = await withSafeImages(it.message);
    onOpenInEditor(msg, it.channelId);
    onOpenChange(false);
    toast.success("Caricato nell'editor", { description: `Canale: #${it.channelName}` });
  };

  const running = job?.status === "running";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] flex-col border-[#232838] bg-[#161923] text-[#DBDEE1] sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><FolderInput className="h-5 w-5 text-[#8ea0ff]" />Importa embed da MEE6 o altri bot</DialogTitle>
          <DialogDescription className="text-[#949BA4]">KALYX legge tutti i canali e raccoglie i messaggi con embed e pulsanti pubblicati da altri bot. Poi li salvi come modelli, li scarichi in un file o li ripubblichi con KALYX, senza riscrivere niente.</DialogDescription>
        </DialogHeader>

        {!items && !running && (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#5865F2]/15"><Search className="h-7 w-7 text-[#8ea0ff]" /></div>
            <p className="max-w-md text-sm text-[#B5BAC1]">La ricerca guarda fino agli ultimi 3000 messaggi di ogni canale. I messaggi dell'altro bot non vengono toccati.</p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button onClick={scan} className={primaryBtn} data-testid="embed-scan"><Search className="mr-2 h-4 w-4" />Cerca in tutto il server</Button>
              <Button variant="outline" onClick={() => fileRef.current?.click()} className={outlineBtn}><Upload className="mr-2 h-4 w-4" />Carica un file</Button>
            </div>
          </div>
        )}
        <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} />

        {running && (
          <div className="space-y-3 py-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-white"><Loader2 className="h-4 w-4 animate-spin text-[#8ea0ff]" />Sto leggendo {job.current ? <>#{job.current}</> : "i canali"}…<span className="ml-auto font-mono text-xs text-[#B5BAC1]">{job.channelsDone}/{job.channelsTotal} canali</span></div>
            <div className="h-2 overflow-hidden rounded-full bg-black/30"><div className="h-full rounded-full bg-[#5865F2] transition-all" style={{ width: `${job.channelsTotal ? (job.channelsDone / job.channelsTotal) * 100 : 0}%` }} /></div>
            <p className="text-xs text-[#949BA4]">Messaggi letti: {job.scanned} · trovati finora: <b className="text-white">{job.items.length}</b></p>
          </div>
        )}

        {items && (
          <>
            <div className="space-y-2.5 border-b border-[#232838] pb-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1 text-xs font-semibold text-[#B5BAC1]">Bot<InfoTip text="I bot che hanno pubblicato i messaggi trovati. Spegni quelli che non ti interessano." /></span>
                {bots.map((b) => {
                  const on = botFilter.has(b.id);
                  return (
                    <button key={b.id} type="button" onClick={() => setBotFilter((s) => { const n = new Set(s); n.has(b.id) ? n.delete(b.id) : n.add(b.id); return n; })}
                      className={`flex items-center gap-1.5 rounded-full border py-0.5 pl-0.5 pr-2.5 text-xs font-semibold ${on ? "border-[#5865F2] bg-[#5865F2]/15 text-white" : "border-[#232838] text-[#6b7185] line-through"}`}>
                      {b.avatar ? <img src={b.avatar} alt="" className="h-5 w-5 rounded-full" /> : <span className="h-5 w-5 rounded-full bg-[#232838]" />}{b.name} ({b.count})
                    </button>
                  );
                })}
                {bots.length === 0 && <span className="text-xs text-[#949BA4]">Nessun messaggio di altri bot trovato.</span>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-[200px] flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6b7185]" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per canale o testo" className="h-9 w-full rounded-md border border-[#232838] bg-[#131620] pl-8 pr-2 text-sm text-white outline-none focus:border-[#5865F2]" />
                </div>
                <button type="button" onClick={() => setSel(allOn ? new Set() : new Set(visible.map((x) => x.id)))} className="text-xs font-semibold text-[#8ea0ff] hover:text-white">{allOn ? "Deseleziona tutti" : "Seleziona tutti"}</button>
                <Button size="sm" disabled={!chosen.length || !!progress} onClick={saveTemplates} className={`h-9 ${primaryBtn}`} data-testid="embed-save-templates"><Save className="mr-1.5 h-4 w-4" />Salva come modelli ({chosen.length})</Button>
                <Button size="sm" variant="outline" disabled={!chosen.length || !!progress} onClick={downloadFile} className={`h-9 ${outlineBtn}`}><Download className="mr-1.5 h-4 w-4" />Scarica file</Button>
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className={`h-9 ${outlineBtn}`}><Upload className="mr-1.5 h-4 w-4" />Carica file</Button>
                <Button size="sm" variant="outline" onClick={scan} className={`h-9 ${outlineBtn}`}><Search className="mr-1.5 h-4 w-4" />Cerca di nuovo</Button>
              </div>
              {progress && <p className="flex items-center gap-2 text-xs text-[#c7cbfb]"><Loader2 className="h-3.5 w-3.5 animate-spin" />{progress.label}… {progress.done}/{progress.total}</p>}
            </div>
            <div className="-mr-2 flex-1 space-y-4 overflow-y-auto pr-2">
              {groups.length === 0 && <p className="py-8 text-center text-sm text-[#949BA4]">Nessun messaggio da mostrare.</p>}
              {groups.map((g) => (
                <div key={g.channelId + g.items[0].id}>
                  <p className="mb-1.5 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-[#B5BAC1]"><Hash className="h-3.5 w-3.5" />{g.channelName}{g.category && <span className="font-normal normal-case text-[#6b7185]">· {g.category}</span>}</p>
                  <div className="space-y-1.5">
                    {g.items.map((it) => (
                      <Item key={it.id} it={it} selected={sel.has(it.id)} onToggle={() => toggle(it.id)} busy={busy === it.id} onOpen={() => openInEditor(it)} onPublish={() => publish(it)} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
